import { useCallback, useState } from 'react';
import { useUpdateRelease } from '@/features/releases/hooks/releases';
import { useApiErrorMessage } from '@/shared/data/errors';
import { useDialogFormState } from '@/ui/hooks/useDialogFormState';
import { useFormErrors } from '@/ui/hooks/useFormErrors';
import type { ReleaseDetailDto, ReleaseListItemDto } from '@flowqa/shared';

type ReleaseEditValues = {
  name: string;
  status: ReleaseDetailDto['status'];
  projectId: string;
  plannedFor: string;
  releasedAt: string;
  tags: string[];
};

type ReleaseEditDialogState = {
  editErrors: ReturnType<typeof useFormErrors<ReleaseEditValues>>;
  editForm: ReturnType<typeof useDialogFormState<ReleaseEditValues>>;
  editSaving: boolean;
  editTarget: ReleaseListItemDto | null;
  onEditClose: () => void;
  onEditRow: (row: ReleaseListItemDto) => void;
  onEditSave: () => Promise<void>;
};

const toDateInputValue = (value: Date | string | null | undefined): string => {
  if (value === null || value === undefined) return '';
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.valueOf()) ? '' : date.toISOString().slice(0, 10);
};

export const useReleaseEditDialog = (): ReleaseEditDialogState => {
  const toErrorMessage = useApiErrorMessage();
  const [editTarget, setEditTarget] = useState<ReleaseListItemDto | null>(null);
  const updateRelease = useUpdateRelease();
  const editForm = useDialogFormState<ReleaseEditValues>({
    name: '',
    status: 'draft',
    projectId: '',
    plannedFor: '',
    releasedAt: '',
    tags: [],
  });
  const editErrors = useFormErrors(editForm);

  const onEditRow = useCallback(
    (row: ReleaseListItemDto): void => {
      setEditTarget(row);
      editForm.setField('name', row.name);
      editForm.setField('status', row.status);
      editForm.setField('projectId', row.projectId);
      editForm.setField('plannedFor', toDateInputValue(row.plannedFor));
      editForm.setField('releasedAt', toDateInputValue(row.releasedAt));
      editForm.setField('tags', row.tags ?? []);
    },
    [editForm],
  );

  const onEditClose = useCallback((): void => {
    if (updateRelease.isPending) return;
    editForm.handleClose(() => {
      setEditTarget(null);
    });
  }, [editForm, updateRelease.isPending]);

  const onEditSave = useCallback(async (): Promise<void> => {
    if (!editTarget) return;
    const valid = editForm.validateRequired('name');
    if (!valid) return;
    try {
      await updateRelease.mutateAsync({
        id: editTarget.id,
        name: editForm.values.name,
        status: editForm.values.status,
        plannedFor: editForm.values.plannedFor || null,
        releasedAt: editForm.values.releasedAt || null,
        tags: editForm.values.tags,
      });
      editForm.handleClose(() => {
        setEditTarget(null);
      });
    } catch (error) {
      if (!editForm.applyApiErrors(error)) {
        editForm.setSubmitError(toErrorMessage(error));
      }
    }
  }, [editForm, editTarget, toErrorMessage, updateRelease]);

  return {
    editErrors,
    editForm,
    editSaving: updateRelease.isPending,
    editTarget,
    onEditClose,
    onEditRow,
    onEditSave,
  };
};
